import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Button,
  InputAdornment,
  makeStyles,
  Paper,
  TextField,
  Theme,
} from '@material-ui/core';
import SearchIcon from '@material-ui/icons/Search';
import { Entity } from '@backstage/catalog-model';
import {
  Content,
  EmptyState,
  Header,
  Page,
  Progress,
} from '@backstage/core-components';
import { useApi } from '@backstage/core-plugin-api';
import { catalogApiRef } from '@backstage/plugin-catalog-react';
import { ToolCategorySection } from './ToolCategorySection';
import {
  groupToolsByCategory,
  INTERNAL_TOOL_FIELDS,
  INTERNAL_TOOLS_FILTER,
} from './utils';

const SEARCH_QUERY_PARAM = 'search';

const getSearchFromQueryString = (queryString: string): string =>
  new URLSearchParams(queryString).get(SEARCH_QUERY_PARAM) ?? '';

const useStyles = makeStyles((theme: Theme) => ({
  search: {
    padding: theme.spacing(1),
    marginBottom: theme.spacing(2),
  },
}));

export const ToolsPage = () => {
  const classes = useStyles();
  const catalogApi = useApi(catalogApiRef);
  const location = useLocation();
  const searchFromQueryString = useMemo(
    () => getSearchFromQueryString(location.search),
    [location.search],
  );
  const [tools, setTools] = useState<Entity[]>([]);
  const [search, setSearch] = useState(searchFromQueryString);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<Error>();

  const loadTools = useCallback(async () => {
    setLoading(true);
    setError(undefined);

    try {
      const { items } = await catalogApi.getEntities({
        fields: INTERNAL_TOOL_FIELDS,
        filter: INTERNAL_TOOLS_FILTER,
      });
      setTools(items);
    } catch (e) {
      setTools([]);
      setError(e as Error);
    } finally {
      setLoading(false);
    }
  }, [catalogApi]);

  useEffect(() => {
    loadTools();
  }, [loadTools]);

  useEffect(() => {
    setSearch(searchFromQueryString);
  }, [searchFromQueryString]);

  const groups = useMemo(
    () => groupToolsByCategory(tools, search),
    [tools, search],
  );

  const renderContent = () => {
    if (loading) {
      return <Progress />;
    }

    if (error) {
      return (
        <EmptyState
          missing="data"
          title="Failed to load internal tools."
          action={
            <Button variant="contained" color="primary" onClick={loadTools}>
              Retry
            </Button>
          }
        />
      );
    }

    if (tools.length === 0) {
      return <EmptyState missing="data" title="No internal tools found." />;
    }

    if (groups.length === 0) {
      return <EmptyState missing="data" title="No tools match your search." />;
    }

    return groups.map(group => (
      <ToolCategorySection
        key={group.id}
        categoryId={group.id}
        title={group.label}
        tools={group.tools}
      />
    ));
  };

  return (
    <Page themeId="home">
      <Header
        title="Internal Tools"
        subtitle="Developer and platform tools available across the company"
      />
      <Content>
        <Paper className={classes.search}>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Search tools..."
            value={search}
            onChange={event => setSearch(event.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            inputProps={{ 'aria-label': 'Search tools' }}
          />
        </Paper>
        {renderContent()}
      </Content>
    </Page>
  );
};
